import { useState, useEffect, useCallback, useRef } from "react";

const DELIMETER = "__";
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
const LOCALSTORAGE_KEY = "dartContainer_productCache";
const LOCALSTORAGE_DURATION = 30 * 60 * 1000; // 30 minutes for persistent cache

// Performance metrics
const cacheMetrics = {
  hits: 0,
  misses: 0,
  requests: 0,
};

// In-memory cache shared across all hook instances
const productCache = new Map();

/**
 * Cache entry structure:
 * {
 *   data: Array<Product>,
 *   timestamp: number,
 *   promise: Promise | null  // For deduplication of in-flight requests
 * }
 */

/**
 * Generate cache key from product codes and options
 */
const getCacheKey = (productCodes, includeAllData, includeInactive) => {
  const codesKey = Array.isArray(productCodes) ? productCodes.sort().join(DELIMETER) : productCodes;
  return `${codesKey}_${includeAllData}_${includeInactive}`;
};

/**
 * Check if cache entry is still valid
 */
const isCacheValid = (cacheEntry, duration = CACHE_DURATION) => {
  if (!cacheEntry) return false;
  const now = Date.now();
  return (now - cacheEntry.timestamp) < duration;
};

/**
 * Load cache from localStorage on initialization
 */
const loadCacheFromStorage = () => {
  try {
    const stored = localStorage.getItem(LOCALSTORAGE_KEY);
    if (!stored) return;

    const parsedCache = JSON.parse(stored);
    Object.entries(parsedCache).forEach(([key, entry]) => {
      // Only restore entries that haven't expired
      if (isCacheValid(entry, LOCALSTORAGE_DURATION)) {
        productCache.set(key, {
          ...entry,
          promise: null, // Don't persist promises
        });
      }
    });
  } catch (error) {
    console.error("Error loading cache from localStorage:", error);
    localStorage.removeItem(LOCALSTORAGE_KEY);
  }
};

/**
 * Save cache to localStorage
 */
const saveCacheToStorage = () => {
  try {
    const cacheObject = {};
    productCache.forEach((value, key) => {
      // Only persist valid entries, exclude promises
      if (isCacheValid(value)) {
        cacheObject[key] = {
          data: value.data,
          timestamp: value.timestamp,
        };
      }
    });
    localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(cacheObject));
  } catch (error) {
    console.error("Error saving cache to localStorage:", error);
  }
};

// Load cache from localStorage on module initialization
loadCacheFromStorage();

/**
 * Custom hook for fetching products by codes with caching
 * 
 * Features:
 * - In-memory caching with configurable TTL
 * - Request deduplication (multiple components fetching same products)
 * - Loading and error states
 * - Automatic refetch capability
 * - Cache invalidation
 * 
 * @param {Array<string>} productCodes - Array of product codes to fetch
 * @param {string} productServletURL - API endpoint URL
 * @param {boolean} includeAllData - Include all product data (optional)
 * @param {boolean} includeInactive - Include inactive products (optional)
 * @param {boolean} enabled - Whether to fetch automatically (default: true)
 * @returns {Object} { products, loading, error, refetch, clearCache }
 */
export const useProductCache = (
  productCodes,
  productServletURL,
  includeAllData = null,
  includeInactive = null,
  enabled = true
) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isMounted = useRef(true);

  // Track if this is the first render to avoid duplicate fetches
  const isFirstRender = useRef(true);

  /**
   * Fetch products from API or cache
   */
  const fetchProducts = useCallback(async () => {
    // Validation
    if (!productCodes || !productServletURL) {
      setProducts([]);
      return [];
    }

    if (!Array.isArray(productCodes) || productCodes.length === 0) {
      setProducts([]);
      return [];
    }

    if (productCodes.every((prod) => prod === undefined || prod === null)) {
      setProducts([]);
      return [];
    }

    const cacheKey = getCacheKey(productCodes, includeAllData, includeInactive);
    const cachedEntry = productCache.get(cacheKey);

    // Track cache performance
    cacheMetrics.requests++;

    // Return cached data if valid
    if (isCacheValid(cachedEntry)) {
      cacheMetrics.hits++;
      if (isMounted.current) {
        setProducts(cachedEntry.data);
        setError(null);
      }
      return cachedEntry.data;
    }

    // Cache miss
    cacheMetrics.misses++;

    // If there's an in-flight request for the same data, wait for it
    if (cachedEntry?.promise) {
      try {
        const data = await cachedEntry.promise;
        if (isMounted.current) {
          setProducts(data);
          setError(null);
        }
        return data;
      } catch (err) {
        if (isMounted.current) {
          setError(err);
        }
        throw err;
      }
    }

    // Start new fetch
    if (isMounted.current) {
      setLoading(true);
      setError(null);
    }

    // Build URL
    const codesParam = productCodes.join(DELIMETER);
    let servletUrl = `${productServletURL}?productCodes=${codesParam}`;
    if (includeAllData) {
      servletUrl += `&includeAllData=${includeAllData}`;
    }
    if (includeInactive) {
      servletUrl += `&includeInactive=${includeInactive}`;
    }

    // Create fetch promise
    const fetchPromise = fetch(servletUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        // Update cache with successful response
        productCache.set(cacheKey, {
          data,
          timestamp: Date.now(),
          promise: null,
        });

        // Persist to localStorage
        saveCacheToStorage();

        if (isMounted.current) {
          setProducts(data);
          setLoading(false);
          setError(null);
        }

        return data;
      })
      .catch((err) => {
        console.error("[useProductCache] Error fetching products:", err, "URL:", servletUrl);
        
        // Remove failed promise from cache
        const entry = productCache.get(cacheKey);
        if (entry?.promise === fetchPromise) {
          productCache.delete(cacheKey);
        }

        if (isMounted.current) {
          setError(err);
          setLoading(false);
          setProducts([]);
        }

        throw err;
      });

    // Store promise in cache for deduplication
    productCache.set(cacheKey, {
      data: cachedEntry?.data || [],
      timestamp: cachedEntry?.timestamp || 0,
      promise: fetchPromise,
    });

    return fetchPromise;
  }, [productCodes, productServletURL, includeAllData, includeInactive]);

  /**
   * Clear cache for specific product codes or entire cache
   */
  const clearCache = useCallback((specificProductCodes = null) => {
    if (specificProductCodes) {
      const key = getCacheKey(specificProductCodes, includeAllData, includeInactive);
      productCache.delete(key);
    } else {
      // Clear all cache
      productCache.clear();
    }
  }, [includeAllData, includeInactive]);

  /**
   * Force refetch (bypass cache)
   */
  const refetch = useCallback(() => {
    clearCache(productCodes);
    return fetchProducts();
  }, [productCodes, fetchProducts, clearCache]);

  // Auto-fetch on mount and when dependencies change
  useEffect(() => {
    if (!enabled) return;

    // Skip fetch on first render if data is already cached
    if (isFirstRender.current) {
      isFirstRender.current = false;
      const cacheKey = getCacheKey(productCodes, includeAllData, includeInactive);
      const cachedEntry = productCache.get(cacheKey);
      if (isCacheValid(cachedEntry)) {
        setProducts(cachedEntry.data);
        return;
      }
    }

    fetchProducts();
  }, [enabled, fetchProducts, productCodes, includeAllData, includeInactive]);

  // Cleanup on unmount
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  return {
    products,
    loading,
    error,
    refetch,
    clearCache,
  };
};

/**
 * Utility function to preload products into cache
 * Useful for prefetching products before they're needed
 */
export const preloadProducts = async (productCodes, productServletURL, includeAllData = null, includeInactive = null) => {
  if (!productCodes || !productServletURL || productCodes.length === 0) {
    return;
  }

  const cacheKey = getCacheKey(productCodes, includeAllData, includeInactive);
  const cachedEntry = productCache.get(cacheKey);

  // Don't preload if already cached and valid
  if (isCacheValid(cachedEntry)) {
    return cachedEntry.data;
  }

  const codesParam = Array.isArray(productCodes) ? productCodes.join(DELIMETER) : productCodes;
  let servletUrl = `${productServletURL}?productCodes=${codesParam}`;
  if (includeAllData) {
    servletUrl += `&includeAllData=${includeAllData}`;
  }
  if (includeInactive) {
    servletUrl += `&includeInactive=${includeInactive}`;
  }

  try {
    const response = await fetch(servletUrl);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    
    productCache.set(cacheKey, {
      data,
      timestamp: Date.now(),
      promise: null,
    });

    return data;
  } catch (error) {
    console.error("Error preloading products:", error);
    return null;
  }
};

/**
 * Clear all product cache (both memory and localStorage)
 */
export const clearAllProductCache = () => {
  productCache.clear();
  try {
    localStorage.removeItem(LOCALSTORAGE_KEY);
  } catch (error) {
    console.error("Error clearing localStorage cache:", error);
  }
  // Reset metrics
  cacheMetrics.hits = 0;
  cacheMetrics.misses = 0;
  cacheMetrics.requests = 0;
};

/**
 * Clear cache for specific product codes
 * @param {Array<string>} productCodes - Product codes to clear from cache
 * @param {boolean} includeAllData - Match includeAllData parameter
 * @param {boolean} includeInactive - Match includeInactive parameter
 */
export const clearProductCache = (productCodes, includeAllData = null, includeInactive = null) => {
  const cacheKey = getCacheKey(productCodes, includeAllData, includeInactive);
  productCache.delete(cacheKey);
  saveCacheToStorage();
};

/**
 * Get cache statistics (useful for debugging and performance monitoring)
 */
export const getCacheStats = () => {
  const entries = Array.from(productCache.entries());
  const validEntries = entries.filter(([, entry]) => isCacheValid(entry));
  
  const hitRate = cacheMetrics.requests > 0 
    ? ((cacheMetrics.hits / cacheMetrics.requests) * 100).toFixed(2)
    : 0;
  
  return {
    totalEntries: entries.length,
    validEntries: validEntries.length,
    expiredEntries: entries.length - validEntries.length,
    cacheDurationMs: CACHE_DURATION,
    performance: {
      totalRequests: cacheMetrics.requests,
      cacheHits: cacheMetrics.hits,
      cacheMisses: cacheMetrics.misses,
      hitRate: `${hitRate}%`,
    },
  };
};

/**
 * Log cache performance metrics to console
 */
export const logCachePerformance = () => {
  const stats = getCacheStats();
  console.group('🎯 Product Cache Performance');
  console.log('Cache Entries:', `${stats.validEntries}/${stats.totalEntries} valid`);
  console.log('Total Requests:', stats.performance.totalRequests);
  console.log('Cache Hits:', stats.performance.cacheHits);
  console.log('Cache Misses:', stats.performance.cacheMisses);
  console.log('Hit Rate:', stats.performance.hitRate);
  console.log('Cache Duration:', `${stats.cacheDurationMs / 1000}s`);
  console.groupEnd();
};

export default useProductCache;
